import {assert} from 'assert-ts';
import type {Schemas} from 'api/dto.generated';
import {transformErrorResponse} from 'store/api';
import {ebsApi} from 'store/ebsApi';
import type {
  Agent,
  NationalAgent,
  NationalAgentType,
  NorafUpdates,
  NorafUpdatesIgnoreListItem,
  SaveAgentOptions,
} from './types';
import {
  mapAgentDto,
  mapAuthority,
  mapNationalAgentDto,
  mapNorafUpdatePageResponse,
  mapNorafUpdatesIgnoreList,
  mapToPostPutAgentDto,
} from './mappers';

const agentApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    // --- Agent CRUD ---
    getAgent: build.query<Agent, Pick<Agent, 'id'>, Schemas.AgentDto>({
      query: ({id}) => `agent/${id}`,
      transformResponse: mapAgentDto,
      transformErrorResponse,
      providesTags: (_, __, {id}) => [{type: 'Agent', id}],
    }),

    getAgentsByIds: build.query<
      Array<Agent>,
      Array<Agent['id']>,
      Array<Schemas.AgentDto>
    >({
      keepUnusedDataFor: 60,
      query: ids => ({
        url: `agent/ids`,
        method: 'POST',
        body: ids,
      }),
      transformResponse: r => r.map(mapAgentDto),
    }),

    putAgent: build.mutation<Agent, {agent: Agent; options: SaveAgentOptions}>({
      query: ({agent, options}) => ({
        url: `agent/${assert(agent.id, 'agent.id is required')}`,
        method: 'PUT',
        body: mapToPostPutAgentDto('PUT', agent, options === 'bbOnly'),
      }),
      transformResponse: (response: Schemas.AgentDto) => mapAgentDto(response),
      transformErrorResponse,
      invalidatesTags: (_, __, {agent}) => [{type: 'Agent', id: agent.id}],
    }),

    postAgent: build.mutation<Agent, {agent: Agent; options: SaveAgentOptions}>(
      {
        query: ({agent, options}) => ({
          url: 'agent',
          method: 'POST',
          body: mapToPostPutAgentDto('POST', agent, options === 'bbOnly'),
        }),
        transformResponse: (response: Schemas.AgentDto) =>
          mapAgentDto(response),
        transformErrorResponse,
      },
    ),

    deleteAgent: build.mutation<void, string>({
      query: id => ({
        url: `agent/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
      invalidatesTags: (_, __, id) => [{type: 'Agent', id}],
    }),

    mergeAgents: build.mutation<
      void,
      {target: Agent; source: Pick<Agent, 'id'>}
    >({
      query: ({target, source}) => ({
        url: `agent/${target.id}/merge/${source.id}`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, {target, source}) => [
        {type: 'Agent', id: target.id},
        {type: 'Agent', id: source.id},
      ],
    }),

    getAgentsByNationalId: build.mutation<Agent[], string[]>({
      query: nationalIds => ({
        url: 'agent/nationalId',
        method: 'POST',
        body: nationalIds,
      }),
      transformResponse: (response: Schemas.AgentDto[]) =>
        response.map(mapAgentDto),
      transformErrorResponse,
    }),

    getAgentByNationalId: build.query<
      NationalAgent,
      string,
      Schemas.AuthorityRegistryWrapperDto
    >({
      query: nationalId => `agent/authorityregistry/${nationalId}`,
      transformResponse: mapNationalAgentDto,
      transformErrorResponse,
    }),

    searchAgentInNationalRegistry: build.mutation<
      NationalAgent[],
      {agentType: NationalAgentType; name?: string; other?: string}
    >({
      query: ({agentType, name, other}) => ({
        url: `agent/search/${agentType.toLowerCase()}`,
        method: 'POST',
        body: {
          name: name?.replaceAll(':', ''),
          other: other?.replaceAll(':', ''),
        },
      }),
      transformResponse: (response: Schemas.AuthorityRegistryWrapperDto[]) =>
        (response ?? [])
          .map(mapNationalAgentDto)
          .filter(a => !!a) as NationalAgent[],
      transformErrorResponse,
    }),

    getAgentNorafDryRun: build.query<
      Schemas.Authority,
      {agent: Agent},
      Schemas.Authority
    >({
      query: ({agent}) => ({
        url: `agent/${agent.id}/norafdryrun`,
        method: 'POST',
        body: mapToPostPutAgentDto('POST', agent, false),
      }),
      transformResponse: mapAuthority,
      transformErrorResponse,
    }),

    // --- Noraf Updates ---
    getNorafUpdates: build.query<
      NorafUpdates,
      {page?: number; size?: number; sort?: string},
      Schemas.NorafUpdatesPageResponse
    >({
      query: ({page, size, sort}) => ({
        url: 'noraf/updates',
        params: {
          page,
          size,
          sort: sort || 'lastUpdated,asc',
        },
      }),
      transformResponse: mapNorafUpdatePageResponse,
      transformErrorResponse,
    }),

    deleteNorafUpdate: build.mutation<void, string>({
      query: id => ({
        url: `noraf/updates/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    deleteNorafUpdateByAgentId: build.mutation<void, string>({
      query: agentId => ({
        url: `noraf/updates/agent/${agentId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    postNorafUpdatesIgnorelist: build.mutation<
      void,
      {nationalId: string; agentId: string; name: string}
    >({
      query: ({nationalId, agentId, name}) => ({
        url: 'noraf/updates/ignorelist',
        method: 'POST',
        body: {nationalId, agentId, name},
      }),
      transformErrorResponse,
    }),

    getNorafUpdatesIgnorelist: build.query<
      NorafUpdatesIgnoreListItem[],
      void,
      Schemas.NorafUpdatesIgnoreListResponse
    >({
      query: () => 'noraf/updates/ignorelist',
      transformResponse: mapNorafUpdatesIgnoreList,
      transformErrorResponse,
    }),

    deleteNorafUpdateIgnorelistItem: build.mutation<void, string>({
      query: id => ({
        url: `noraf/updates/ignorelist/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetAgentQuery,
  useLazyGetAgentQuery,
  useLazyGetAgentsByIdsQuery,
  usePutAgentMutation,
  usePostAgentMutation,
  useDeleteAgentMutation,
  useMergeAgentsMutation,
  useGetAgentsByNationalIdMutation,
  useGetAgentByNationalIdQuery,
  useSearchAgentInNationalRegistryMutation,
  useLazyGetAgentNorafDryRunQuery,
  useGetNorafUpdatesQuery,
  useLazyGetNorafUpdatesQuery,
  useDeleteNorafUpdateMutation,
  useDeleteNorafUpdateByAgentIdMutation,
  usePostNorafUpdatesIgnorelistMutation,
  useGetNorafUpdatesIgnorelistQuery,
  useDeleteNorafUpdateIgnorelistItemMutation,
} = agentApi;
